import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BASE_URL, HEADERS } from "../../utils/constant";
import { toast } from "react-toastify";

const EXTRA_USER_PRICE = 5.5;
const EXTRA_SMS_PRICE = 0.03;

const CUSTOM_PLAN_FEATURES = [
  "CRM & Lead Management",
  "Ticket Management System (Internal + Customer)",
  "Customer & Sub-Customer Login",
  "Customer Ticket Creation",
  "Field Service Requests (FSR)",
  "Inventory & Stock Management",
  "Material Request & Issue Management",
  "Stock Expenditure / Usage Tracking",
  "Expense & Expenditure Management",
  "AMC (Annual Maintenance Contract) Management",
  "User & Role Management",
  "Attendance, Punch-in / Punch-out & Leave",
  "Employee & Technician Tracking",
  "Service Requests Management",
  "Advanced Reports & Analytics",
];

const CustomSubscriptionTab = ({ activePlan }) => {
  const navigate = useNavigate();
  const adminId = localStorage.getItem("userId");

  const [loading, setLoading] = useState(false);
  const [customData, setCustomData] = useState(null);

  const [users, setUsers] = useState(0);
  const [sms, setSms] = useState(0);
  const [billingCycle, setBillingCycle] = useState("yearly");

  useEffect(() => {
    const fetchCustomPlan = async () => {
      try {
        const { data } = await axios.get(
          `${BASE_URL}/users/subscriptions/${adminId}/custom-subscription/`,
          { headers: HEADERS },
        );

        if (data) {
          setCustomData(data);
          setUsers(Number(data.pending_employee_limit));
          setSms(Number(data.pending_sms_limit));
          setBillingCycle(data.billingCycle || "yearly");
        } else {
          toast.error("No Custom Subscription Available");
        }
      } catch {
        setCustomData(null);
      }
    };

    fetchCustomPlan();
  }, [adminId]);

  const FeatureBlock = () => (
    <div
      className="p-3 rounded mb-3"
      style={{
        background: "linear-gradient(135deg, #0d6efd15, #19875410)",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">All Features Included</h5>
        <span className="badge bg-success">Premium</span>
      </div>

      <ul className="small mb-2">
        {CUSTOM_PLAN_FEATURES.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>

      {!customData && (
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() =>
            (window.location.href =
              "mailto:fieldsathi@gmail.com?subject=Custom Plan Inquiry")
          }
        >
          Contact Sales
        </button>
      )}
    </div>
  );

  if (!customData) {
    return (
      <div className="container mt-4">
        <h4 className="mb-3">Custom Subscription</h4>
        <FeatureBlock />

        <div className="card p-4 text-center border-0 shadow-sm">
          <h6 className="fw-semibold">Tailored for Your Business</h6>
          <p className="small text-muted mb-0">
            Get custom pricing, flexible users & SMS limits, and full access to
            all modules. Contact our sales team to activate your custom plan.
          </p>
        </div>
      </div>
    );
  }

  const BASE_PRICE = Number(customData.pending_total_cost);
  

  const monthlySubtotal = BASE_PRICE;

  const totalCost = Math.round(monthlySubtotal);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePayment = async () => {
    try {
      setLoading(true);
      const loaded = await loadRazorpay();
      if (!loaded) return toast.error("Razorpay failed to load");

      const { data } = await axios.post(
        `${BASE_URL}/users/subscriptions/activate-subscription/`,
        {
          plan: "Custom",
          user_limit: users,
          sms_limit: sms,
          billing_cycle: billingCycle,
          totalCost,
        },
        { headers: HEADERS },
      );

      new window.Razorpay({
        key: "rzp_live_SSyTAyUNyXIR2P",
        order_id: data.order_id,
        amount: data.amount,
        currency: "INR",
        name: "Field Sathi",
        description: "Custom Subscription",
        handler: async (response) => {
          await axios.post(
            `${BASE_URL}/users/subscriptions/confirm-subscription/`,
            {
              order_id: data.order_id,
              payment_id: response.razorpay_payment_id,
              
            },
            { headers: HEADERS },
          );

          toast.success("Plan activated successfully");
          navigate("/dashboard");
        },
        theme: { color: "#0d6efd" },
      }).open();
    } catch {
      toast.error("Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-3">Custom Subscription Plan</h4>

      <FeatureBlock />
      <div className="row justify-content-center mt-4">
        <div className="col-lg-6">
          <div className="card shadow border-0 rounded-3 p-4">
            <h6 className="fw-bold mb-3">Payment Summary</h6>

            {(() => {
              const GST_PERCENT = 18;

              const subtotal = Number(BASE_PRICE);
              const gstAmount = Math.round((subtotal * GST_PERCENT) / 100);
              const totalCost = Math.round(subtotal + gstAmount);

              return (
                <>
                  <div className="small">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Plan</span>
                      <span>Custom</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Users</span>
                      <span>{users}</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>SMS Limit</span>
                      <span>{sms}</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal</span>
                      <span>₹{subtotal}</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>GST (18%)</span>
                      <span>₹{gstAmount}</span>
                    </div>
                  </div>

                  <hr />

                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted">Total Payable</small>
                      <h4 className="text-primary fw-bold mb-0">
                        ₹{totalCost}
                      </h4>
                    </div>

                    <button
                      className="btn btn-success px-4"
                      onClick={handlePayment}
                      disabled={loading}
                    >
                      {loading ? "Processing..." : "Proceed to Payment"}
                    </button>
                  </div>

                  <div className="text-center small text-muted mt-3">
                    🔒 Secure payment via Razorpay
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSubscriptionTab;
